import React, { useState, useEffect, useRef } from 'react';
import './SpeedAdjustmentPopup.scss';

interface SpeedAdjustmentPopupProps {
  visible: boolean;
  onClose: () => void;
  onSpeedChange: (speed: number) => void;
  initialSpeed?: number;
  position?: {
    top: number;
    left: number;
  };
  resetTrigger?: number; // 重置触发器，当值变化时重置到initialSpeed
}

const SpeedAdjustmentPopup: React.FC<SpeedAdjustmentPopupProps> = ({
  visible,
  onClose,
  onSpeedChange,
  initialSpeed = 1.0,
  position = { top: 0, left: 0 },
  resetTrigger
}) => {
  const [speed, setSpeed] = useState(initialSpeed);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);


  useEffect(() => {
    setSpeed(initialSpeed);
  }, [initialSpeed]);

  // 监听重置触发器，当值变化时重置到initialSpeed
  useEffect(() => {
    if (resetTrigger !== undefined) {
      setSpeed(initialSpeed);
    }
  }, [resetTrigger, initialSpeed]);

  // 清理定时器
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, []);

  const handleSpeedChange = (newSpeed: number) => {
    // 限制倍速范围在0.5到2.0之间
    const clampedSpeed = Math.max(0.5, Math.min(2.0, newSpeed));
    setSpeed(clampedSpeed);
    
    // 清除之前的定时器
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    
    // 设置防抖，300ms后调用API
    debounceTimer.current = setTimeout(() => {
      onSpeedChange(clampedSpeed);
    }, 300);
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSpeed = parseFloat(e.target.value);
    handleSpeedChange(newSpeed);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // 移除末尾的'x'字符
    const numericValue = value.replace('x', '');
    const newSpeed = parseFloat(numericValue);
    
    if (!isNaN(newSpeed)) {
      handleSpeedChange(newSpeed);
    }
  };

  const handleIncrement = () => {
    handleSpeedChange(speed + 0.1);
  };

  const handleDecrement = () => {
    handleSpeedChange(speed - 0.1);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!visible) return null;

  return (
    <div className="speed-adjustment-overlay" onClick={onClose}>
      <div 
        className="speed-adjustment-popup"
        style={{
          top: position.top,
          left: position.left
        }}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={handleKeyDown}
        tabIndex={-1}
      >
        {/* 标题 */}
        <div className="popup-title">
          调节语速
        </div>

        {/* 倍速控制区域 */}
        <div className="speed-control-section">
          {/* 滑块 */}
          <div className="speed-slider-container">
            <input
              type="range"
              min="0.5"
              max="2.0"
              step="0.1"
              value={speed}
              onChange={handleSliderChange}
              className="speed-slider"
            />
            <div className="slider-ticks">
              <div className="tick" style={{ left: '0%' }}></div>
              <div className="tick" style={{ left: '33.33%' }}></div>
              <div className="tick" style={{ left: '66.66%' }}></div>
              <div className="tick" style={{ left: '100%' }}></div>
            </div>
          </div>

          {/* 数值输入框和调节按钮 */}
          <div className="speed-input-container">
            <input
              type="text"
              value={`${speed.toFixed(1)}x`}
              onChange={handleInputChange}
              className="speed-input"
            />
            <div className="speed-spinner">
              <button
                type="button"
                className="spinner-btn spinner-up"
                onClick={handleIncrement}
                disabled={speed >= 2.0}
              >
                ▲
              </button>
              <button
                type="button"
                className="spinner-btn spinner-down"
                onClick={handleDecrement}
                disabled={speed <= 0.5}
              >
                ▼
              </button>
            </div>
          </div>
        </div>

       
      </div>
    </div>
  );
};

export default SpeedAdjustmentPopup;
